import { useParams, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

interface DynamicRouteOptions {
  path?: string;
  params?: Record<string, string>;
  query?: Record<string, string | null | undefined>;
  options?: {
    clearQuery?: boolean;
    resetQuery?: boolean;
  };
}

export const useDynamicRoute = () => {
  const router = useRouter();
  const routeParams = useParams() as Record<string, string>;
  const routeQuery = Object.fromEntries(useSearchParams());

  /**
   * Formats a dynamic route href with new params and query
   * @NOTE This function will not work for spread route params like `/[...slug]`
   */
  const formatHref = ({
    path,
    params,
    query,
    options = {},
  }: DynamicRouteOptions) => {
    const routePath = path ?? router.pathname; // includes dynamic route params like `/[sport]/[state]/`

    // swaps out dynamic route params with new values
    params = { ...routeParams, ...params };

    let pathname = Object.entries(params).reduce((acc, [key, value]) => {
      const constructString = '\\[\\[?\\.{0,3}(' + key + ')+\\]?\\]';
      const check = new RegExp(constructString, 'g');

      if (!value) return acc.replace(check, '');

      return acc.replace(check, value);
    }, routePath);

    if (!pathname.endsWith('/') && pathname !== '/') {
      pathname += '/';
    }

    // handles query state
    query = options.clearQuery
      ? {}
      : options.resetQuery
        ? { ...query }
        : { ...routeQuery, ...query };

    // filters out null and undefined values
    const filteredQuery = Object.entries(query).reduce(
      (acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value;
        }

        return acc;
      },
      {} as Record<string, string>,
    );

    const search = new URLSearchParams(filteredQuery).toString();

    // builds new href
    return `${pathname}${search ? `?${search}` : ''}`;
  };

  return {
    formatHref,
    // for convenience
    router,
    routeParams,
    routeQuery,
  };
};
